import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { getMyTimesheet } from '../../services/timesheetService';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import timesheetStatusColors from '../../utils/timesheetStatusColors';
import TIMESHEET_STATUS_LABEL_KEYS, { canonicalTimesheetStatus } from '../../utils/timesheetStatusLabels';
import formatMinutes from '../../utils/formatMinutes';

const monthNames = {
  tr: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'],
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  ru: ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'],
  kk: ['Қаңтар', 'Ақпан', 'Наурыз', 'Сәуір', 'Мамыр', 'Маусым', 'Шілде', 'Тамыз', 'Қыркүйек', 'Қазан', 'Қараша', 'Желтоқсан'],
  az: ['Yanvar', 'Fevral', 'Mart', 'Aprel', 'May', 'İyun', 'İyul', 'Avqust', 'Sentyabr', 'Oktyabr', 'Noyabr', 'Dekabr'],
};

const formatDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatDayDisplay = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return String(d.getDate()).padStart(2, '0');
};

export default function MyTimesheetPage() {
  const { t, i18n } = useTranslation();
  const today = new Date();

  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const startDate = useMemo(() => formatDate(new Date(year, month, 1)), [year, month]);
  const endDate = useMemo(() => formatDate(new Date(year, month + 1, 0)), [year, month]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMyTimesheet({ startDate, endDate });
      const raw = res?.data?.data ?? res?.data;
      setData(Array.isArray(raw) ? raw : []);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePrev = () => {
    if (month === 0) { setMonth(11); setYear((y) => y - 1); }
    else setMonth((m) => m - 1);
  };

  const handleNext = () => {
    if (month === 11) { setMonth(0); setYear((y) => y + 1); }
    else setMonth((m) => m + 1);
  };

  const handleToday = () => {
    const now = new Date();
    setYear(now.getFullYear());
    setMonth(now.getMonth());
  };

  const lang = i18n.language?.substring(0, 2) || 'tr';
  const months = monthNames[lang] || monthNames.tr;
  const todayStr = formatDate(today);

  const getStatusBadge = (status) => {
    if (!status) return null;
    const key = canonicalTimesheetStatus(status);
    const colors = timesheetStatusColors[key] || { bg: '#f1f5f9', text: '#64748b' };
    const labelKey = TIMESHEET_STATUS_LABEL_KEYS[key] || `timesheet.status${key.charAt(0).toUpperCase() + key.slice(1)}`;
    return <StatusBadge $bg={colors.bg} $color={colors.text}>{t(labelKey)}</StatusBadge>;
  };

  const isWeekend = (dateStr) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    return d.getDay() === 0 || d.getDay() === 6;
  };

  const isToday = (dateStr) => {
    if (!dateStr) return false;
    return dateStr.substring(0, 10) === todayStr;
  };

  return (
    <Container>
      <Breadcrumbs items={[{ label: t('myTimesheet.title') }]} />

      <Header>
        <NavSection>
          <NavButton onClick={handlePrev}>&#9664;</NavButton>
          <MonthLabel>{months[month]} {year}</MonthLabel>
          <NavButton onClick={handleNext}>&#9654;</NavButton>
        </NavSection>
        <TodayButton onClick={handleToday}>{t('mySchedule.today')}</TodayButton>
      </Header>

      <TableWrapper>
        <StyledTable>
          <thead>
            <tr>
              <Th>{t('myTimesheet.date')}</Th>
              <Th>{t('myTimesheet.shift')}</Th>
              <Th>{t('myTimesheet.expected')}</Th>
              <Th>{t('myTimesheet.actual')}</Th>
              <Th>{t('myTimesheet.overtime')}</Th>
              <Th>{t('myTimesheet.late')}</Th>
              <Th>{t('myTimesheet.earlyLeave')}</Th>
              <Th>{t('myTimesheet.status')}</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <Td colSpan={8} style={{ textAlign: 'center', padding: '40px' }}>...</Td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <Td colSpan={8} style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>-</Td>
              </tr>
            ) : (
              data.map((row) => {
                const dateStr = row.date?.substring(0, 10);
                const weekend = isWeekend(dateStr);
                const todayRow = isToday(dateStr);
                return (
                  <Tr key={dateStr} $weekend={weekend} $isToday={todayRow}>
                    <Td>{formatDayDisplay(dateStr)}</Td>
                    <Td>{row.workHourName || '-'}</Td>
                    <Td>{formatMinutes(row.expectedMinutes)}</Td>
                    <Td>{formatMinutes(row.actualMinutes)}</Td>
                    <Td>{formatMinutes(row.overtimeMinutes)}</Td>
                    <Td>{formatMinutes(row.lateMinutes)}</Td>
                    <Td>{formatMinutes(row.earlyLeaveMinutes)}</Td>
                    <Td>{getStatusBadge(row.status)}</Td>
                  </Tr>
                );
              })
            )}
          </tbody>
        </StyledTable>
      </TableWrapper>
    </Container>
  );
}

// ─── Styled Components ───────────────────────────────────────────

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  font-family: ${({ theme }) => theme.metrics.font.family};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
`;

const NavSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const NavButton = styled.button`
  background: none;
  border: 1px solid ${(p) => p.theme.colors?.border || '#E8ECF0'};
  border-radius: 6px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 14px;
  color: ${(p) => p.theme.colors?.text?.primary || '#004242'};
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${(p) => p.theme.colors?.bgColor || '#F5F7FA'};
  }
`;

const MonthLabel = styled.span`
  font-size: ${(p) => p.theme.metrics?.font?.size?.lg || '18px'};
  font-weight: ${(p) => p.theme.metrics?.font?.weight?.bold || 700};
  color: ${(p) => p.theme.colors?.text?.primary || '#004242'};
  min-width: 180px;
  text-align: center;
`;

const TodayButton = styled.button`
  background: ${(p) => p.theme.colors?.primary?.main || '#0291EF'};
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: ${(p) => p.theme.metrics?.font?.size?.sm || '14px'};
  font-weight: ${(p) => p.theme.metrics?.font?.weight?.semiBold || 600};
  font-family: ${(p) => p.theme.metrics?.font?.family || "'Nunito Sans', sans-serif"};

  &:hover {
    opacity: 0.9;
  }
`;

const TableWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 16px 16px;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${(p) => p.theme.metrics?.font?.size?.sm || '13px'};
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 12px;
  background: ${(p) => p.theme.colors?.bgColor || '#F5F7FA'};
  color: ${(p) => p.theme.colors?.text?.secondary || '#64748b'};
  font-weight: ${(p) => p.theme.metrics?.font?.weight?.semiBold || 600};
  border-bottom: 1px solid #E8ECF0;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 10px 12px;
  border-bottom: 1px solid #E8ECF0;
  white-space: nowrap;
`;

const Tr = styled.tr`
  background: ${(p) =>
    p.$isToday ? '#eff6ff' : p.$weekend ? '#f8fafc' : '#fff'};
  border-left: ${(p) => (p.$isToday ? '3px solid #0291EF' : '3px solid transparent')};

  &:hover {
    background: ${(p) => (p.$isToday ? '#dbeafe' : '#f1f5f9')};
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
`;
